import { CancelRounded as CancelRoundedIcon, CheckCircleRounded as CheckCircleRoundedIcon, CloseRounded as CloseRoundedIcon, InsertDriveFileRounded as InsertDriveFileRoundedIcon, ReplayRounded as ReplayRoundedIcon } from '@mui/icons-material'
import { Box, IconButton, LinearProgress, Stack, Typography } from '@mui/material'
import { memo, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { type ImageUploadQueueItem } from './ImageUploadQueue'
import { formatImageFileSize, formatImageFileType } from './imageFileMetadata'
import ErrorIcon from '@mui/icons-material/Error';

interface Props {
  upload: ImageUploadQueueItem
  onCancel: (id: string) => void
  onRetry: (id: string) => void
  onRemove: (id: string) => void
}

const ImageUploadQueueRow = memo(function ImageUploadQueueRow(props: Props) {
  const [t] = useTranslation('account-v2')

  const uploadState = useMemo(() => {
    const progress = Math.min(Math.max(props.upload.progress, 0), 100)
    const isUploading = props.upload.status === 'uploading'
    const isProcessing = props.upload.status === 'processing'
    const isWorking = isUploading || isProcessing
    const isDone = props.upload.status === 'done'
    const hasFailed = props.upload.status === 'error' || props.upload.status === 'canceled'
    const progressKnown = props.upload.progressKnown !== false
    const progressVariant: 'indeterminate' | 'determinate' = isProcessing || (isUploading && !progressKnown)
      ? 'indeterminate'
      : 'determinate'
    let statusText = t('Uploading')

    if (isUploading && progressKnown) {
      statusText = t('{{progress}}%', { progress })
    } else if (props.upload.status === 'processing') {
      statusText = t('Processing')
    } else if (props.upload.status === 'done') {
      statusText = t('Uploaded')
    } else if (props.upload.status === 'error') {
      statusText = t('Upload failed')
    } else if (props.upload.status === 'canceled') {
      statusText = t('Canceled')
    }

    return {
      canRetry: hasFailed && props.upload.canRetry !== false,
      hasFailed,
      isDone,
      isWorking,
      progress,
      progressVariant,
      statusColor: hasFailed ? 'error.main' : 'text.secondary',
      statusText,
    }
  }, [
    t,
    props.upload.canRetry,
    props.upload.progress,
    props.upload.progressKnown,
    props.upload.status,
  ])

  const fileMetadata = useMemo(() => {
    const fileType = formatImageFileType(props.upload.file.type, props.upload.name)

    return [fileType, formatImageFileSize(props.upload.size)].filter(Boolean).join(' • ')
  }, [props.upload.file.type, props.upload.name, props.upload.size])

  const progressColor = useMemo(() => (
    props.upload.status === 'done' ? 'success' : (uploadState.hasFailed ? 'error' : 'primary')
  ), [props.upload.status, uploadState.hasFailed])

  const onCancelClick = useCallback(() => {
    props.onCancel(props.upload.id)
  }, [props.onCancel, props.upload.id])

  const onRetryClick = useCallback(() => {
    props.onRetry(props.upload.id)
  }, [props.onRetry, props.upload.id])

  const onRemoveClick = useCallback(() => {
    props.onRemove(props.upload.id)
  }, [props.onRemove, props.upload.id])

  return <Box
    component="div"
    sx={{
      backgroundColor: 'rgba(0, 0, 0, 0.035)',
      borderRadius: 1,
      p: 1,
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="center">
      {props.upload.previewUrl ? <Box
        component="div"
        sx={{
          aspectRatio: '1 / 1',
          bgcolor: 'rgba(0, 0, 0, 0.08)',
          borderRadius: 0.5,
          flexShrink: 0,
          overflow: 'hidden',
          position: 'relative',
          width: {
            xs: 44,
            sm: 52,
          },
        }}
      >
        <Box
          component="img"
          src={props.upload.previewUrl}
          alt=""
          sx={{
            display: 'block',
            height: '100%',
            objectFit: 'cover',
            width: '100%',
          }}
        />
      </Box> : <Box
        component="div"
        sx={{
          alignItems: 'center',
          aspectRatio: '1 / 1',
          bgcolor: 'rgba(0, 0, 0, 0.08)',
          borderRadius: 0.5,
          color: 'text.secondary',
          display: 'flex',
          flexShrink: 0,
          justifyContent: 'center',
          width: {
            xs: 44,
            sm: 52,
          },
        }}
      >
        <InsertDriveFileRoundedIcon fontSize="small" />
      </Box>}

      <Stack spacing={0.25} sx={{ flexGrow: 1, minWidth: 0 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{
            xs: 0.25,
            sm: 1
          }}
          alignItems="flex-start"
          sx={{ minWidth: 0 }}
        >
          <Typography variant="body2" noWrap sx={{ flexGrow: 1, minWidth: 0, maxWidth: '100%' }}>{props.upload.name}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
            {fileMetadata}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ height: '10px' }}>
          <LinearProgress
            variant={uploadState.progressVariant}
            value={uploadState.progress}
            color={progressColor}
            sx={{
              flexGrow: 1,
              height: 6,
            }}
          />
          <Stack
            spacing={0.5}
            direction="row"
            sx={{
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            {uploadState.isDone ? <CheckCircleRoundedIcon color="success" fontSize="small" /> : null}
            {uploadState.hasFailed ? <ErrorIcon color="error" fontSize="small" /> : null}
            <Typography
              variant="caption"
              sx={{
                color: uploadState.statusColor,
                flexShrink: 0,
                textAlign: 'right',
                display: {
                  xs: 'none',
                  sm: 'block',
                }
              }}
            >
              {uploadState.statusText}
            </Typography>
          </Stack>
        </Stack>
        {props.upload.error ? <Typography variant="caption" sx={{ color: 'error.main' }}>
          {props.upload.error}
        </Typography> : null}
      </Stack>

      <div>
        <Stack direction="column" spacing={0.25} alignItems="center" sx={{ flexShrink: 0, my: -1 }}>
          {uploadState.isWorking ? <IconButton
            size="small"
            onClick={onCancelClick}
            aria-label={t('Cancel upload')}
          ><CancelRoundedIcon fontSize="small" /></IconButton> : null}

          {!uploadState.isWorking ? <IconButton
            size="small"
            onClick={onRemoveClick}
            aria-label={t('Remove upload')}
          ><CloseRoundedIcon fontSize="small" /></IconButton> : null}

          {uploadState.canRetry ? <IconButton
            size="small"
            color="secondary"
            onClick={onRetryClick}
            aria-label={t('Retry upload')}
          ><ReplayRoundedIcon fontSize="small" /></IconButton> : null}
        </Stack>
      </div>
    </Stack>
  </Box>
})

export default ImageUploadQueueRow
